import { Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import Divider from '@mui/material/Divider';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import ExpandableTextArea from 'components/ExpandableTextArea';
import { useNotification } from 'hook/useNotification';
import { useExportConfigurationMutation } from 'services/ioConfigurationApi';

export default function ExportConfigurationDetails({
  assistants = [],
  prompts = [],
  scripts = [],
  onReset = () => {}
}) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();
  const [exportConfiguration, { isLoading: isExporting }] = useExportConfigurationMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      description: ''
    }
  });

  const clearField = (field) => {
    setValue(field, '');
  };

  const resetExportConfiguration = () => {
    onReset();
    reset({
      description: ''
    });
  };

  const handleExportSubmit = async (data) => {
    try {
      const exportData = {
        ...data,
        assistantIds: assistants.map((assistant) => assistant.id),
        promptIds: prompts.map((prompt) => prompt.id),
        scriptCodes: scripts.map((script) => script.code)
      };

      const result = await exportConfiguration(exportData).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addNotification({
        type: 'success',
        title: msg({ id: 'notification.success.title' }),
        message: msg({ id: 'page.exportConfiguration.export.success' })
      });
    } catch (error) {
      console.error('Error exporting configuration:', error);
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: msg({ id: 'page.exportConfiguration.export.error' })
      });
    }
  };

  const handleReset = () => {
    resetExportConfiguration();
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(handleExportSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%', width: '100%' }}
    >
      <Stack display="flex" direction="row">
        <IosShareIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({ id: 'page.exportConfiguration.details.title' })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDBox sx={{ height: '120px' }}>
              <ExpandableTextArea
                minRows={5}
                field={field}
                error={!!errors.description}
                helperText={errors.description ? errors.description.message : ''}
                clearField={() => clearField('description')}
                label={msg({ id: 'field.description.label' })}
                control={control}
              />
            </MDBox>
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton
          type="button"
          fullWidth
          color="secondary"
          onClick={handleReset}
          disabled={isExporting}
        >
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton
          type="submit"
          fullWidth
          color="primary"
          loading={isExporting}
          disabled={isExporting}
        >
          {msg({ id: 'button.export' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

ExportConfigurationDetails.propTypes = {
  assistants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired
    })
  ),
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired
    })
  ),
  scripts: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired
    })
  ),
  onReset: PropTypes.func
};
